import * as React from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { RootStore, withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { PaymentCycle_BIMap } from 'services/BiLogger/BiLoggerTypes';
import { AppHeader } from '../AppHeader';
import { ErrorBoundary } from '../ErrorBoundaries/ErrorBoundary';
import { Notification } from 'components/Notification/Notification';
import s from './App.scss';
import { GlobalStyles } from './GlobalStyles';
import { MultiProductApp } from './MultiProductApp';
import { BreakpointProvider } from 'providers/BreakpointProvider';
import { StickyHeaderHeightProvider } from 'providers/StickyHeaderHeightProvider';
import { isEligibleForVoucher } from 'utils';
import { Footer } from '../Footer/Footer';

export interface AppProps extends InjectedExperimentsProps {
  rootStore: RootStore;
}

export const AppComponent: React.FC<AppProps> = ({ rootStore }) => {
  const { theme } = usePageModeContext();
  React.useEffect(() => {
    const {
      appContext,
      currentCycle,
      currentProduct,
      productsPrices,
      planType,
      currentPlanInOfferingMode,
      intent,
    } = rootStore.appStore;
    const {
      biLogger,
      fedopsLogger,
      currencyCode,
      currentPlanDetails,
      requestParamsMap,
    } = appContext;
    const abTesting = window.screen
      ? `width:${window.screen.width};height:${window.screen.height}; version: v3`
      : '';
    biLogger.logPlansPageLoadedBiEvent({
      abTesting,
      ppType: planType,
      cycle: PaymentCycle_BIMap[currentCycle],
      productId: currentProduct.id,
      productGuid: currentProduct.guid,
      pricing: productsPrices,
      currency: currencyCode,
      changePlanType: currentPlanInOfferingMode,
      currentProductId: currentPlanDetails?.planId ?? null,
      intent,
      intentDomainName:
        (isEligibleForVoucher(requestParamsMap.domainName) &&
          requestParamsMap.domainName) ||
        null,
    });
    fedopsLogger.endAppRenderedInteraction();
  }, []);

  const { setScrollToTopRef, currentStep } = rootStore.appStore;
  return (
    <>
      <GlobalStyles />
      <>
        <BreakpointProvider>
          <StickyHeaderHeightProvider>
            <AppHeader currentStep={currentStep} />
            <ErrorBoundary>
              <div className={classNames(s.mainContent, theme)}>
                <Notification />
                <span
                  ref={setScrollToTopRef}
                  data-hook='scroll-ref'
                  className={s.scrollAncor}
                />
                <MultiProductApp />
                <Footer />
              </div>
            </ErrorBoundary>
          </StickyHeaderHeightProvider>
        </BreakpointProvider>
      </>
    </>
  );
};

export const App = withInjectedRootStore(
  withExperiments(observer(AppComponent)),
);
